// SVG overlay on the snapshot: renders polygons, handles selection, and (for
// admins) polygon drawing plus vertex dragging. Points are stored normalized
// (0..1); the SVG viewBox tracks the image's natural pixel size so overlay
// and image scale together exactly.
import type { Point, PolygonView } from '../shared/types';
import { api } from './api';
import { loadPayload, refreshAnnotated } from './data';
import { $ } from './dom';
import { state } from './state';
import { toast } from './toast';

const SVG_NS = 'http://www.w3.org/2000/svg';

let svg: SVGSVGElement;
let stage: HTMLElement;
let img: HTMLImageElement;
let natural = { w: 1600, h: 1200 };

let drawing = false;
let draft: Point[] = [];
let hoverPoint: Point | null = null;
let renderQueued = false;

export function isDrawing(): boolean {
  return drawing;
}

export function initOverlay(): void {
  svg = $('#overlay');
  stage = $('#frameStage');
  img = $('#frameImg');

  img.addEventListener('load', () => {
    natural = { w: img.naturalWidth || 1600, h: img.naturalHeight || 1200 };
    svg.setAttribute('viewBox', `0 0 ${natural.w} ${natural.h}`);
    requestRender();
  });

  state.addEventListener('payload', requestRender);
  state.addEventListener('selection', requestRender);
  state.addEventListener('admin', () => {
    if (!state.admin && drawing) cancelDraw();
    requestRender();
  });
  state.addEventListener('frame', () => {
    if (drawing) cancelDraw(); // a new snapshot invalidates the draft
  });

  stage.addEventListener('click', (e) => {
    if (drawing) {
      handleDrawClick(e);
    } else if (e.target === svg || e.target === img) {
      state.selectPolygon(null);
    }
  });
  stage.addEventListener('dblclick', (e) => {
    if (drawing) {
      e.preventDefault();
      void finishDraw();
    }
  });
  stage.addEventListener('mousemove', (e) => {
    if (!drawing) return;
    hoverPoint = eventToPoint(e);
    requestRender();
  });

  const btnDraw = $<HTMLButtonElement>('#btnDraw');
  btnDraw.addEventListener('click', () => {
    if (drawing) cancelDraw();
    else startDraw();
  });
}

/** Returns true if the key was consumed by draw mode. */
export function handleDrawKey(e: KeyboardEvent): boolean {
  if (!drawing) return false;
  if (e.key === 'Escape') {
    cancelDraw();
    return true;
  }
  if (e.key === 'Enter') {
    void finishDraw();
    return true;
  }
  return false;
}

function startDraw(): void {
  if (!state.admin || !state.currentFrame) return;
  drawing = true;
  draft = [];
  hoverPoint = null;
  stage.classList.add('drawing');
  $('#drawHint').removeAttribute('hidden');
  $('#btnDraw').classList.add('active');
  requestRender();
}

export function cancelDraw(): void {
  drawing = false;
  draft = [];
  hoverPoint = null;
  stage.classList.remove('drawing');
  $('#drawHint').setAttribute('hidden', '');
  $('#btnDraw').classList.remove('active');
  requestRender();
}

function handleDrawClick(e: MouseEvent): void {
  const p = eventToPoint(e);
  if (draft.length >= 3 && screenDistance(p, draft[0]) < 12) {
    void finishDraw();
    return;
  }
  draft.push(p);
  requestRender();
}

async function finishDraw(): Promise<void> {
  const points = draft.slice();
  const frame = state.currentFrame;
  cancelDraw();
  if (points.length < 3 || !frame) return;
  try {
    const { id } = await api.admin.createPolygon(frame.ms, points, null);
    toast('Polygon created — attach a term to it', 'ok');
    await loadPayload();
    state.selectPolygon(id);
    void refreshAnnotated();
  } catch (err) {
    toast(err instanceof Error ? err.message : 'failed to create polygon', 'err');
  }
}

// ------------------------------------------------------------ rendering

function requestRender(): void {
  if (renderQueued) return;
  renderQueued = true;
  requestAnimationFrame(() => {
    renderQueued = false;
    render();
  });
}

function render(): void {
  svg.textContent = '';
  const polygons = [...(state.payload?.polygons ?? [])];
  // big polygons first so small ones stay clickable on top
  polygons.sort((a, b) => area(b.points) - area(a.points));
  for (const poly of polygons) {
    const node = document.createElementNS(SVG_NS, 'polygon');
    node.setAttribute('class', `poly${poly.id === state.selectedPolygonId ? ' selected' : ''}`);
    node.setAttribute('points', toSvgPoints(poly.points));
    node.addEventListener('click', (e) => {
      if (drawing) return;
      e.stopPropagation();
      state.selectPolygon(poly.id);
    });
    svg.appendChild(node);
  }

  if (drawing && draft.length > 0) {
    const preview = hoverPoint ? [...draft, hoverPoint] : draft;
    const node = document.createElementNS(SVG_NS, draft.length >= 3 ? 'polygon' : 'polyline');
    node.setAttribute('class', 'draft');
    node.setAttribute('points', toSvgPoints(preview));
    svg.appendChild(node);
    draft.forEach((p, i) => svg.appendChild(vertexCircle(p, i === 0 ? 'vertex first' : 'vertex')));
  }

  const selected = state.selectedPolygon;
  if (!drawing && state.admin && selected) {
    selected.points.forEach((p, i) => {
      const circle = vertexCircle(p, 'vertex');
      attachDragHandler(circle, selected, i);
      svg.appendChild(circle);
    });
  }
}

function vertexCircle(p: Point, className: string): SVGCircleElement {
  const circle = document.createElementNS(SVG_NS, 'circle');
  circle.setAttribute('class', className);
  circle.setAttribute('cx', String(p[0] * natural.w));
  circle.setAttribute('cy', String(p[1] * natural.h));
  circle.setAttribute('r', String(Math.max(4, natural.w * 0.006)));
  return circle;
}

function attachDragHandler(circle: SVGCircleElement, poly: PolygonView, index: number): void {
  circle.addEventListener('pointerdown', (e) => {
    if (!state.admin) return;
    e.preventDefault();
    e.stopPropagation();
    const points = poly.points.map((p) => [...p] as Point);
    const rect = svg.getBoundingClientRect();
    let moved = false;

    const onMove = (ev: PointerEvent) => {
      moved = true;
      points[index] = [
        clamp01((ev.clientX - rect.left) / rect.width),
        clamp01((ev.clientY - rect.top) / rect.height),
      ];
      poly.points = points;
      requestRender();
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      if (!moved) return;
      api.admin.updatePolygon(poly.id, { points }).catch((err: unknown) => {
        toast(err instanceof Error ? err.message : 'failed to save polygon', 'err');
        void loadPayload();
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, { once: true });
  });
}

// ------------------------------------------------------------ geometry

function eventToPoint(e: MouseEvent): Point {
  const rect = svg.getBoundingClientRect();
  return [clamp01((e.clientX - rect.left) / rect.width), clamp01((e.clientY - rect.top) / rect.height)];
}

function screenDistance(a: Point, b: Point): number {
  const rect = svg.getBoundingClientRect();
  const dx = (a[0] - b[0]) * rect.width;
  const dy = (a[1] - b[1]) * rect.height;
  return Math.hypot(dx, dy);
}

function toSvgPoints(points: Point[]): string {
  return points.map((p) => `${p[0] * natural.w},${p[1] * natural.h}`).join(' ');
}

function area(points: Point[]): number {
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % points.length];
    sum += x1 * y2 - x2 * y1;
  }
  return Math.abs(sum) / 2;
}

function clamp01(n: number): number {
  return Math.min(Math.max(n, 0), 1);
}
